import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import { Grid } from "@mui/material";
import { fetchReportsCreationStatus } from "actions/reportActions";
import clsx from "clsx";
import { reportsSelectors, selectIsDataGenerationComplete } from "features/reports";
import { useAppSelector, useAuth } from "hooks/hooks";
import { useEffect, useRef } from "react";
import { ReportCreationStatus } from "utilities/constants/enums";
import {
	DATA_GENERATION_MAX_DURATION_IN_MINUTES,
	DATA_GENERATION_POLLING_MAX_DURATION_IN_MINUTES,
} from "utilities/dataGenerationStatus";
import { getLoadingStatusIcon } from "utilities/loadingStatus";
import styles from "./DataGenerationStatuses.module.scss";

interface DataGenerationStatusViewInputProps {
	portalHasAccess: boolean;
	setIsPreparingData: (isPreparingData: boolean) => void;
	timeElapsed: number;
}

export const DataGenerationStatusView = ({
	portalHasAccess,
	setIsPreparingData,
	timeElapsed,
}: DataGenerationStatusViewInputProps) => {
	const tasks = useAppSelector(reportsSelectors.selectAll);
	const { dispatch, auth } = useAuth();
	const hasForceEnabledRef = useRef(false);

	const dataGenerationComplete = useAppSelector(selectIsDataGenerationComplete);
	const hasReachedMaxDuration = timeElapsed > (DATA_GENERATION_MAX_DURATION_IN_MINUTES + 1) * 60;
	const hasReachedPollingMaxDuration =
		timeElapsed > DATA_GENERATION_POLLING_MAX_DURATION_IN_MINUTES * 60;

	useEffect(() => {
		const correctTimeToTryFetch = timeElapsed === 1 || timeElapsed % 10 === 0;

		if (correctTimeToTryFetch && !dataGenerationComplete && !hasReachedPollingMaxDuration) {
			dispatch(fetchReportsCreationStatus({ auth }));
		}
		// else if (correctTimeToTryFetch && hasReachedPollingMaxDuration) {
		// }
	}, [
		timeElapsed,
		dataGenerationComplete,
		hasReachedMaxDuration,
		hasReachedPollingMaxDuration,
		dispatch,
		auth,
	]);

	// Move the setIsPreparingData call into a useEffect to prevent infinite loops
	useEffect(() => {
		if (dataGenerationComplete || hasReachedMaxDuration || hasReachedPollingMaxDuration) {
			setIsPreparingData(false);
		} else if (hasForceEnabledRef.current) {
			// Don't re-enable preparing data if we've already force-enabled the button
			setIsPreparingData(false);
		}
	}, [
		dataGenerationComplete,
		hasReachedMaxDuration,
		hasReachedPollingMaxDuration,
		setIsPreparingData,
		// Note: timeElapsed removed from dependencies to prevent overriding force-enable logic
	]);

	// FORCE ENABLE CHECK: Using useEffect to avoid setState during render
	useEffect(() => {
		const shouldForceEnable = timeElapsed > 360; // 6 minutes for production
		if (shouldForceEnable && !hasForceEnabledRef.current) {
			hasForceEnabledRef.current = true;
			setIsPreparingData(false);
		}
	}, [timeElapsed, setIsPreparingData]);

	const getIcon = (status: ReportCreationStatus) => {
		if (!portalHasAccess) {
			return <QueryBuilderIcon fontSize="large" />;
		}

		return getLoadingStatusIcon(status);
	};

	// Define the sort order mapping
	const statusSortOrder: { [key in ReportCreationStatus]: number } = {
		[ReportCreationStatus.Passed]: 1,
		[ReportCreationStatus.Running]: 2,
		[ReportCreationStatus.Failed]: 3,
		[ReportCreationStatus.Skipped]: 4,
		[ReportCreationStatus.NotRun]: 5,
	};

	// Sort the tasks based on the status order
	const sortedTasks = [...tasks].sort((a, b) => {
		return statusSortOrder[a.Status] - statusSortOrder[b.Status];
	});

	return (
		<Grid container direction="column" alignItems="center">
			{/* Show loading message when no tasks are available yet */}
			{tasks.length === 0 && timeElapsed > 5 && (
				<Grid container direction="row" alignItems="center" className={styles.taskRow}>
					<Grid item>
						<div className={styles.loadingIcon}>
							{getLoadingStatusIcon(ReportCreationStatus.Running)}
						</div>
					</Grid>
					<Grid item ml={3}>
						<span>Generating data for dashboards</span>
					</Grid>
				</Grid>
			)}

			{sortedTasks.map(({ Status, Name, DisplayName }) => (
				<Grid
					container
					direction="row"
					alignItems="center"
					className={clsx(styles.taskRow, {
						[styles.taskRowFaded]: Status === ReportCreationStatus.NotRun,
					})}
					key={Name}
				>
					<Grid
						item
						className={clsx({
							[styles.whiteIcon]:
								Status === ReportCreationStatus.NotRun ||
								Status === ReportCreationStatus.Running ||
								Status === ReportCreationStatus.Skipped,
							[styles.greenIcon]: Status === ReportCreationStatus.Passed,
							[styles.redIcon]: Status === ReportCreationStatus.Failed,
						})}
					>
						{getIcon(Status)}
					</Grid>
					<Grid item ml={3}>
						<span>{DisplayName}</span>
					</Grid>
				</Grid>
			))}

			{/* Show timeout message when polling has stopped */}
			{hasReachedPollingMaxDuration && !dataGenerationComplete && (
				<Grid container direction="column" alignItems="center" mt={2} p={2}>
					<Grid item>
						<span style={{ color: "#ff9800", fontSize: "14px", textAlign: "center" }}>
							⏰ Data generation is taking longer than expected.
							<br />
							You can safely enter the portal now - additional data will be available
							shortly.
						</span>
					</Grid>
				</Grid>
			)}

			{/* Show message when 6-minute force-enable has activated */}
			{hasForceEnabledRef.current &&
				!dataGenerationComplete &&
				!hasReachedPollingMaxDuration && (
					<Grid container direction="column" alignItems="center" mt={2} p={2}>
						<Grid item>
							<span
								style={{ color: "#2196f3", fontSize: "14px", textAlign: "center" }}
							>
								✨ Setup is taking longer than usual, but you can proceed safely.
								<br />
								Data generation will continue in the background after you enter the
								portal.
							</span>
						</Grid>
					</Grid>
				)}
		</Grid>
	);
};
